import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from "react";
import { useEffect, useState } from "react";
import { Empty, Progress, Spin } from "antd";
import { _i, __ } from "../../../utils";
import { observer } from "mobx-react";
import { useLocationQuery, useScannerFromSitemap } from "../../../hooks";
import { useStores } from "../../../store";
import { CheckCircleTwoTone, IssuesCloseOutlined } from "@ant-design/icons";
import { useUpsellModal } from "@devowl-wp/react-cookie-banner-admin";
const ScannerProgress = observer(()=>{
    const { optionStore: { others: { isPro } }, scannerStore: { canShowResults, foundScanResultsCount, needsAttentionCount } } = useStores();
    const [statusCheckInterval, setStatusCheckInterval] = useState(undefined);
    const { status, currentJob, total, percent, remaining, cancelling, handleStart: originalHandleStart, handleCancel, step, stepText } = useScannerFromSitemap(statusCheckInterval, "rcb-scan-list");
    const disableScanButton = step !== "idle";
    const { isLicensed, modal: licenseActivationModal, open: openLicenseActivation } = useUpsellModal({
        title: __("Scanner requires up-to-date search data from the Service Cloud"),
        description: __("The scanner can automatically search your website for used services. To do this, it needs an up-to-date database of search data for services, which must be downloaded from Real Cookie Banner's Service Cloud. The data will be downloaded locally to your server, so your website visitors will not need to connect to the cloud.") + (isPro ? "" : ` ${__("You can activate your free licence at any time, without any costs.")}`),
        mode: "license-activation",
        feature: "scanner",
        assetName: "service-cloud.svg",
        assetMaxHeight: 200
    });
    const handleStart = useCallback(()=>{
        if (isLicensed) {
            originalHandleStart();
        } else {
            openLicenseActivation();
        }
    }, [
        originalHandleStart,
        isLicensed,
        openLicenseActivation
    ]);
    // Allow to force a template by query argument so the template center is disabled
    const { start } = useLocationQuery();
    useEffect(()=>{
        if (start) {
            handleStart();
        }
    }, []);
    // Provide "live" refresh while scanning the complete website
    useEffect(()=>{
        setStatusCheckInterval(remaining > 0 && currentJob && total ? 5000 : undefined);
    }, [
        remaining,
        currentJob,
        total
    ]);
    return remaining === undefined ? /*#__PURE__*/ _jsx(Spin, {
        style: {
            display: "block"
        }
    }) : /*#__PURE__*/ _jsxs("div", {
        className: "wp-clearfix",
        children: [
            licenseActivationModal,
            canShowResults && /*#__PURE__*/ _jsx("div", {
                style: {
                    float: "left",
                    margin: "5px 10px"
                },
                children: needsAttentionCount === 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(CheckCircleTwoTone, {
                            twoToneColor: "#52c41a"
                        }),
                        "\xa0",
                        __("All recommendations applied")
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(IssuesCloseOutlined, {
                            style: {
                                color: "#dba617"
                            }
                        }),
                        "\xa0",
                        __("%d of %d recommendations applied", foundScanResultsCount - needsAttentionCount, foundScanResultsCount)
                    ]
                })
            }),
            status === "failed" ? /*#__PURE__*/ _jsx(Empty, {
                style: {
                    clear: "both"
                },
                description: __("Scan failed"),
                image: /*#__PURE__*/ _jsx(Progress, {
                    type: "circle",
                    width: 100,
                    percent: 100,
                    status: "exception"
                })
            }) : status === "done" ? /*#__PURE__*/ _jsx(Empty, {
                style: {
                    clear: "both"
                },
                description: __("Scan completed"),
                image: /*#__PURE__*/ _jsx(Progress, {
                    type: "circle",
                    width: 100,
                    percent: 100
                })
            }) : remaining > 0 && currentJob && total ? /*#__PURE__*/ _jsx(Empty, {
                style: {
                    clear: "both"
                },
                description: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            children: _i(__("Currently scanning {{code}}%s{{/code}} (%d of %d pages scanned)...", currentJob.data.url, total - remaining, total), {
                                code: /*#__PURE__*/ _jsx("code", {})
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "notice notice-info inline below-h2 notice-alt",
                            style: {
                                margin: "10px 0 0 0",
                                display: "inline-block"
                            },
                            children: /*#__PURE__*/ _jsx("p", {
                                children: __("You can add already found services, edit your website or have a coffee in the meantime. The scanner runs in the background.")
                            })
                        })
                    ]
                }),
                image: /*#__PURE__*/ _jsx(Progress, {
                    type: "circle",
                    width: 100,
                    percent: percent
                }),
                children: /*#__PURE__*/ _jsx("button", {
                    className: "button button-primary",
                    onClick: handleCancel,
                    disabled: cancelling,
                    children: __("Cancel scan")
                })
            }) : canShowResults ? /*#__PURE__*/ _jsx("button", {
                id: "rcb-btn-scan-start",
                className: "button button-primary alignright",
                onClick: handleStart,
                disabled: disableScanButton,
                style: {
                    marginBottom: 10,
                    display: step !== "done" ? undefined : "none"
                },
                children: stepText
            }) : /*#__PURE__*/ _jsx(Empty, {
                description: __("Scan your website for services that may set cookies or process personal data to obtain consent."),
                children: /*#__PURE__*/ _jsx("button", {
                    className: "button button-primary",
                    onClick: handleStart,
                    disabled: disableScanButton,
                    children: stepText
                })
            })
        ]
    });
});
export { ScannerProgress };
